import React, {useEffect, useState} from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  message,
  Row,
  Col,
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import Api from 'configs';

export const Settings: React.FC = () => {
  const [surchargeForm] = Form.useForm();
  const [cancelEnabled, setCancelEnabled] = useState<boolean>(true);
  const [loadingSurcharge, setLoadingSurcharge] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);

  // Load initial settings
  const fetchSettings = async () => {
    try {
      // 1. Surcharge config (Task N - cố định VNĐ)
      const resSurcharge = await Api.get('/system-config/surcharge');
      if (resSurcharge?.data) {
        surchargeForm.setFieldsValue({
          label: resSurcharge.data.label || 'Phụ phí dịch vụ',
          value: resSurcharge.data.value ?? 0,
        });
      }

      // 2. Cancel settings (Task J)
      const resCancel = await Api.get('/system-config/cancel-settings');
      if (resCancel?.data) {
        setCancelEnabled(resCancel.data.driverCancelFeatureEnabled ?? true);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Save Surcharge (Task N - Cố định)
  const handleSaveSurcharge = async () => {
    try {
      setLoadingSurcharge(true);
      const values = await surchargeForm.validateFields();
      await Api.put('/system-config/surcharge', {
        ...values,
        type: 'fixed',
      });
      message.success('Cập nhật cấu hình phụ phí thành công');
    } catch (error) {
      message.error('Lỗi khi lưu cấu hình phụ phí');
    } finally {
      setLoadingSurcharge(false);
    }
  };

  // Save Cancel Setting (Task J)
  const handleToggleCancel = async (checked: boolean) => {
    try {
      setLoadingCancel(true);
      await Api.put('/system-config/cancel-settings', {
        driverCancelFeatureEnabled: checked,
      });
      setCancelEnabled(checked);
      message.success(
        checked
          ? 'Đã BẬT tính năng phạt tỉ lệ hủy đơn'
          : 'Đã TẮT tính năng phạt tỉ lệ hủy đơn',
      );
    } catch (error) {
      message.error('Lỗi khi cập nhật cài đặt tỉ lệ hủy');
    } finally {
      setLoadingCancel(false);
    }
  };

  return (
    <div className="container mx-auto p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <SettingOutlined /> Cài đặt chung hệ thống
        </h2>
      </div>

      <Row gutter={[24, 24]}>
        {/* TASK N: Phụ phí dịch vụ cố định */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <span className="font-semibold text-base text-blue-600">
                💰 Cấu hình Phụ phí dịch vụ (Khách trả - Cố định)
              </span>
            }
            className="shadow-sm border rounded-lg h-full">
            <p className="text-gray-500 text-sm mb-4">
              Phụ phí được cộng trực tiếp dưới dạng số tiền cố định (VNĐ) vào tổng tiền đơn hàng của khách.
            </p>
            <Form form={surchargeForm} layout="vertical">
              <Form.Item
                label="Tên hiển thị phụ phí"
                name="label"
                rules={[{required: true, message: 'Nhập tên hiển thị phụ phí'}]}>
                <Input placeholder="Ví dụ: Phụ phí dịch vụ, Phí thời tiết..." />
              </Form.Item>

              <Form.Item
                label="Số tiền phụ phí cố định (VNĐ)"
                name="value"
                rules={[{required: true, message: 'Nhập số tiền phụ phí cố định'}]}>
                <InputNumber
                  className="w-full"
                  min={0}
                  step={1000}
                  formatter={val =>
                    `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  addonAfter="VNĐ"
                  placeholder="Ví dụ: 5000"
                />
              </Form.Item>

              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={loadingSurcharge}
                onClick={handleSaveSurcharge}
                className="w-full mt-2">
                Lưu cấu hình phụ phí
              </Button>
            </Form>
          </Card>
        </Col>

        {/* TASK J: Tỉ lệ phạt hủy đơn tài xế */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <span className="font-semibold text-base text-orange-600">
                🚫 Tính năng Tỉ lệ hủy đơn tài xế
              </span>
            }
            className="shadow-sm border rounded-lg h-full">
            <p className="text-gray-600 mb-4 text-sm">
              Khi bật tính năng này, hệ thống sẽ tự động ghi nhận lượt hủy của tài xế
              theo ngày và tháng, hiển thị cảnh báo khi tài xế hủy vượt ngưỡng quy định (≥ 5%).
            </p>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md border mb-6">
              <div>
                <span className="font-semibold text-base block">
                  Trạng thái tính năng
                </span>
                <span className="text-sm text-gray-500">
                  {cancelEnabled ? 'Đang kích hoạt' : 'Đang tạm tắt'}
                </span>
              </div>
              <Switch
                checked={cancelEnabled}
                loading={loadingCancel}
                onChange={handleToggleCancel}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
